<script lang="ts" setup>
import { Comment, Fragment, Text, computed, useSlots, type VNode } from 'vue';
import { useActionButtonAgentTool } from '../../edito/action-bar/actionButtonAgentTool';
import { actionBaseClasses, actionDisabledClasses, actionDisplayClasses, actionDropdownDisabledClasses, actionSizeClasses, actionToolbarDisabledClasses, actionVariantClasses, type ActionDisplay, type ActionSize, type ActionVariant } from './classes';

defineOptions({
    inheritAttrs: false
});

const instance = getCurrentInstance();

const props = withDefaults(defineProps<{
    variant?: ActionVariant | string;
    size?: ActionSize;
    display?: ActionDisplay;
    tag?: string;
    type?: 'button' | 'submit' | 'reset';
    to?: any;
    block?: boolean;
    pill?: boolean;
    square?: boolean;
    squared?: boolean;
    disabled?: boolean;
    icon?: string;
    iconPosition?: 'left' | 'right';
    iconClass?: string | string[] | Record<string, boolean>;
    link?: string;
    loading?: boolean;
    noBorder?: boolean;
    unstyled?: boolean;
    active?: boolean;
    activeClass?: string;
    stateKey?: string | number | boolean | null;
    text?: string | null;
    title?: string | null;
    confirm?: string;
    confirmationMode?: 'popover' | 'inline';
    confirmText?: string;
    preventClose?: boolean;
    confirmAttrs?: any;
    nowrap?: boolean;
    back?: boolean;
    submit?: boolean;
    toolbar?: boolean;
    tool?: string;
    stopPropagation?: boolean;
}>(), {
    variant: 'secondary',
    size: 'md',
    display: 'default',
    tag: 'button',
    type: 'button',
    to: null,
    icon: null,
    iconPosition: 'left',
    iconClass: () => ([]),
    link: null,
    activeClass: 'active',
    stateKey: null,
    text: null,
    title: null,
    confirm: '',
    confirmationMode: undefined,
    confirmText: '[[[Potwierdź]]]',
    confirmAttrs: {},
    nowrap: true,
    back: false,
    submit: false,
    toolbar: false,
    tool: '',
    stopPropagation: false
});

const emit = defineEmits<{
    (e: 'click', event: MouseEvent): void;
}>();

const slots = useSlots();
const attrs = useAttrs();
const element = ref<any>(null);
const stateContentContainer = ref<HTMLElement | null>(null);
const stateContent = ref<HTMLElement | null>(null);
const stateContentWidth = ref<number | null>(null);
const stateWidthAnimation = ref<Animation | null>(null);
const statePositionAnimations = new Map<HTMLElement, Animation>();
const loaded = ref(false);
const inlineAsking = ref(false);
const floating = ref(false);
const hideDropdown = inject<(() => void) | null>('hideDropdown', null);
const dropdown = ref(hideDropdown !== null);
const actionBarFormId = inject<Ref<string> | null>('actionBarFormId', null);

const attrsClasses = computed(() => attrs.class);
const interactionDisabled = computed(() => props.disabled || props.loading);
const ariaDisabled = computed(() => interactionDisabled.value ? 'true' : attrs['aria-disabled']);
const dataDisabled = computed(() => interactionDisabled.value ? true : attrs['data-disabled']);

const condition = computed(() =>
{
    if (instance?.parent && 'condition' in instance.parent)
        return (instance.parent as any).condition;

    return false;
});

const dropdownAction = computed(() => props.display === 'default' && condition.value && dropdown.value);
const floatingAction = computed(() => props.display === 'default' && condition.value && floating.value);
const backAction = computed(() => props.display === 'default' && props.back && !dropdownAction.value && !floatingAction.value);

function getSlotContent(nodes: VNode[]): VNode[]
{
    return nodes.flatMap((node) =>
    {
        if (node.type === Comment)
            return [];

        if (node.type === Text)
        {
            if (typeof node.children === 'string' && node.children.trim().length > 0)
                return [node];

            return [];
        }

        if (node.type === Fragment && Array.isArray(node.children))
            return getSlotContent(node.children as VNode[]);

        return [node];
    });
}

function isIdeoIconNode(node: VNode)
{
    const type = node.type as any;

    return type?.name === 'IdeoIcon' || type?.['__name'] === 'IdeoIcon';
}

function getDefaultSlotContent()
{
    return getSlotContent(slots.default?.() || []);
}

async function measureStateContent()
{
    if (!stateful.value)
    {
        stateWidthAnimation.value?.cancel();
        stateContentWidth.value = null;

        return;
    }

    await nextTick();

    if (!stateContent.value)
        return;

    updateStateWidth(stateContent.value, false);
}

function cancelStatePositionAnimations()
{
    for (const animation of statePositionAnimations.values())
        animation.cancel();
    statePositionAnimations.clear();
}

function updateStateWidth(content: HTMLElement, animated = true)
{
    const container = stateContentContainer.value;
    if (!stateful.value || props.block)
    {
        stateWidthAnimation.value?.cancel();
        cancelStatePositionAnimations();
        stateContentWidth.value = null;
        container?.style.removeProperty('width');
        return;
    }

    const previousWidth = stateContentWidth.value === null ? null : container?.getBoundingClientRect().width ?? stateContentWidth.value;
    const layers = container ? [...container.querySelectorAll<HTMLElement>('[data-action-state-content]')] : [];
    const previousPositions = new Map(layers.map(layer => [layer, layer.getBoundingClientRect().left]));
    const nextWidth = Math.ceil(content.getBoundingClientRect().width);

    // Ukryty dialog zwraca zero; utrwalenie tego pomiaru zostawia po otwarciu sam border przycisku.
    if (nextWidth <= 0)
    {
        stateWidthAnimation.value?.cancel();
        cancelStatePositionAnimations();
        stateContentWidth.value = null;
        container?.style.removeProperty('width');
        return;
    }

    stateWidthAnimation.value?.cancel();
    cancelStatePositionAnimations();
    stateContentWidth.value = nextWidth;
    if (!container)
        return;

    if (previousWidth !== null)
        container.style.width = `${previousWidth}px`;
    const naturalPositions = new Map(layers.map(layer => [layer, layer.getBoundingClientRect().left]));
    container.style.width = `${nextWidth}px`;
    if (!animated || previousWidth === null || previousWidth === nextWidth || prefersReducedMotion())
        return;

    // Zmiana szerokości w flex-end/center przesuwa sam przycisk; warstwy zachowują ekranową oś X.
    const finalPositions = new Map(layers.map(layer => [layer, layer.getBoundingClientRect().left]));
    const timing = { duration: 300, easing: 'cubic-bezier(.16, 1.1, .3, 1)' };
    const startTime = document.timeline.currentTime;
    stateWidthAnimation.value = container.animate([
        { width: `${previousWidth}px` },
        { width: `${nextWidth}px` }
    ], timing);
    if (typeof startTime === 'number')
        stateWidthAnimation.value.startTime = startTime;

    for (const layer of layers)
    {
        const finalLeft = finalPositions.get(layer) ?? layer.getBoundingClientRect().left;
        const targetLeft = layer === content ? finalLeft : previousPositions.get(layer) ?? finalLeft;
        const endOffset = targetLeft - finalLeft;
        const startOffset = targetLeft - (naturalPositions.get(layer) ?? finalLeft);
        if (Math.abs(startOffset) < 0.01 && Math.abs(endOffset) < 0.01)
            continue;

        const animation = layer.animate([
            { left: `${startOffset}px` },
            { left: `${endOffset}px` }
        ], { ...timing, fill: 'both' });
        if (typeof startTime === 'number')
            animation.startTime = startTime;
        statePositionAnimations.set(layer, animation);
        const complete = () =>
        {
            if (statePositionAnimations.get(layer) === animation)
                statePositionAnimations.delete(layer);
            if (animation.playState === 'finished')
                animation.cancel();
        };
        animation.addEventListener('finish', complete, { once: true });
        animation.addEventListener('cancel', complete, { once: true });
    }
}

function prefersReducedMotion()
{
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function completeStateAnimation(animation: Animation, done: () => void)
{
    let completed = false;

    function complete()
    {
        if (completed)
            return;

        completed = true;
        done();
    }

    animation.addEventListener('finish', complete, { once: true });
    animation.addEventListener('cancel', complete, { once: true });
}

function enterStateContent(contentElement: Element, done: () => void)
{
    updateStateWidth(contentElement as HTMLElement);

    if (!stateful.value || prefersReducedMotion())
    {
        done();

        return;
    }

    const animation = contentElement.animate([
        { opacity: 0, translate: '0 90%', filter: 'blur(3px)' },
        { opacity: 1, translate: '0 0', filter: 'blur(0)' }
    ], {
        duration: 260,
        easing: 'cubic-bezier(.16, 1.1, .3, 1)',
        fill: 'both'
    });

    completeStateAnimation(animation, done);
}

function leaveStateContent(contentElement: Element, done: () => void)
{
    contentElement.setAttribute('aria-hidden', 'true');

    if (!stateful.value || prefersReducedMotion())
    {
        done();

        return;
    }

    const animation = contentElement.animate([
        { opacity: 1, translate: '0 0', filter: 'blur(0)', offset: 0 },
        { opacity: 0, translate: '0 0', filter: 'blur(0)', offset: 0.4 },
        { opacity: 0, translate: '0 -70%', filter: 'blur(2px)', offset: 1 }
    ], {
        duration: 120,
        easing: 'cubic-bezier(.6, 0, 1, 1)',
        fill: 'both'
    });

    completeStateAnimation(animation, done);
}

const rootAttrs = computed(() =>
{
    const result = { ...attrs };

    delete result.class;
    delete result['aria-disabled'];
    delete result['data-disabled'];

    return result;
});

const routeTarget = computed(() => props.to ?? props.link);

const componentTag = computed(() =>
{
    if (routeTarget.value)
        return 'router-link';

    return props.tag;
});

const buttonType = computed(() =>
{
    if (componentTag.value !== 'button')
        return null;

    if (props.submit)
        return 'submit';

    return props.type;
});

const utilityClasses: Record<string, string> = {
    'ms-auto': 'ms-auto',
    'ms-0': 'ms-0',
    'ms-1': 'ms-1',
    'ms-2': 'ms-2',
    'ms-3': 'ms-3',
    'ms-4': 'ms-4',
    'ms-5': 'ms-5',
    'me-auto': 'me-auto',
    'me-0': 'me-0',
    'me-1': 'me-1',
    'me-2': 'me-2',
    'me-3': 'me-3',
    'me-4': 'me-4',
    'me-5': 'me-5'
};

const variantParts = computed(() => (props.variant ?? 'secondary').split(' ').filter(part => part.length > 0));
const resolvedVariant = computed<ActionVariant>(() =>
{
    if (backAction.value)
        return 'secondary';

    const variant = variantParts.value.find(part => part in actionVariantClasses);

    return (variant ?? 'secondary') as ActionVariant;
});

const variantUtilityClasses = computed(() => variantParts.value
    .filter(part => !(part in actionVariantClasses))
    .map(part => utilityClasses[part] ?? part));

const resolvedSize = computed<ActionSize>(() =>
{
    if (props.size in actionSizeClasses)
        return props.size;

    return 'md';
});

const resolvedDisplay = computed<ActionDisplay>(() =>
{
    if (props.display !== 'default')
        return props.display;

    if (dropdownAction.value)
        return 'dropdown-inline';

    if (floatingAction.value)
        return 'floating';

    return 'default';
});

const aiVariant = computed(() => resolvedVariant.value === 'fancy');
const toolbarDisplay = computed(() => resolvedDisplay.value === 'toolbar' || resolvedDisplay.value === 'back');
const dropdownDisplay = computed(() => resolvedDisplay.value === 'dropdown' || resolvedDisplay.value === 'dropdown-inline');
const inlineTextConfirmation = computed(() => resolvedConfirmationMode.value === 'inline' && !!confirmationMessage.value && !iconOnly.value
    && (textVisible.value || getDefaultSlotContent().some(node => !isIdeoIconNode(node))));
const stateful = computed(() => props.stateKey !== null || (resolvedConfirmationMode.value === 'inline' && !!confirmationMessage.value));
const contentStateKey = computed(() => stateful.value ? JSON.stringify([props.stateKey, inlineAsking.value, props.text, props.icon]) : 'default');
const loaderTone = computed(() =>
{
    if (['secondary', 'light', 'dark', 'link', 'none', 'outline', 'text', 'outline-secondary', 'outline-light', 'outline-dark'].includes(resolvedVariant.value))
        return 'muted';

    return 'current';
});
const buttonText = computed(() => dropdownAction.value ? props.text || props.title : props.text);
const textVisible = computed(() =>
{
    if (dropdownAction.value)
        return true;

    if (!buttonText.value)
        return false;

    return !floatingAction.value && !backAction.value;
});
const resolvedIcon = computed(() => inlineAsking.value ? 'check' : backAction.value ? props.icon || 'arrow-left' : props.icon);

const iconOnly = computed(() =>
{
    if (props.square || backAction.value)
        return true;

    if (resolvedDisplay.value !== 'default')
        return false;

    const slotContent = getDefaultSlotContent();

    if (props.icon)
        return slotContent.length === 0 && !buttonText.value;

    return !buttonText.value && slotContent.length === 1 && isIdeoIconNode(slotContent[0]);
});

const automaticTrashConfirmation = computed(() =>
{
    const content = getDefaultSlotContent();
    const iconContentOnly = !buttonText.value && (props.icon ? content.length === 0 : content.length === 1 && isIdeoIconNode(content[0]));
    if ((!iconOnly.value && !iconContentOnly) || routeTarget.value || attrs.href)
        return false;
    const slotIcon = content.find(isIdeoIconNode);
    const name = String(props.icon || slotIcon?.props?.name || '').replace(/[-_\s]/gu, '').toLowerCase();
    return name === 'trash' || name === 'trash2';
});
const confirmationMessage = computed(() => props.confirm || (automaticTrashConfirmation.value ? $t('[[[Potwierdź usunięcie]]]') : ''));
const resolvedConfirmationMode = computed(() => props.confirmationMode ?? (automaticTrashConfirmation.value ? 'inline' : 'popover'));


const customClasses = computed(() =>
{
    if (props.unstyled)
    {
        return {
            [props.activeClass]: props.active,
            'w-full': props.block
        };
    }

    return [
        actionBaseClasses,
        actionVariantClasses[resolvedVariant.value],
        resolvedDisplay.value === 'default' ? actionSizeClasses[resolvedSize.value] : '',
        actionDisplayClasses[resolvedDisplay.value],
        variantUtilityClasses.value,
        props.variant === 'vivid' && props.active && !props.disabled ? '!border-transparent !bg-[#6d28d9] !text-white dark:!bg-[#c4b5fd] dark:!text-[#211238]' : '',
        resolvedDisplay.value === 'toolbar' && props.active ? '!bg-[var(--color-accent-solid)] !text-accent-fg hover:!bg-[color-mix(in_oklch,var(--color-accent-solid)_94%,black)]' : '',
        props.disabled ? (toolbarDisplay.value ? actionToolbarDisabledClasses : resolvedDisplay.value === 'dropdown' || resolvedDisplay.value === 'dropdown-inline' ? actionDropdownDisabledClasses : actionDisabledClasses) : '',
        props.loading ? 'cursor-wait' : '',
        props.nowrap ? '' : '!whitespace-normal',
        stateful.value ? '!px-0 overflow-hidden' : '',
        {
            [props.activeClass]: props.active,
            'w-full': props.block,
            '!rounded-full': props.pill,
            'size-[30px] max-h-[30px] min-h-[30px] max-w-[30px] min-w-[30px] flex-none p-0': iconOnly.value,
            '!rounded-none': props.squared,
            '!border-0': props.noBorder
        }
    ];
});

const iconSize = computed(() =>
{
    if (dropdownDisplay.value)
        return 13;

    if (toolbarDisplay.value || iconOnly.value)
        return 16;

    return 15;
});
const iconDimensionClasses = computed(() => iconSize.value === 13 ? 'size-[13px] min-h-[13px] min-w-[13px] !m-0' : iconSize.value === 16 ? 'size-[16px] min-h-[16px] min-w-[16px]' : 'size-[15px] min-h-[15px] min-w-[15px]');
const childIconDimensionClasses = computed(() => iconSize.value === 13
    ? '[&>svg]:!size-[13px] [&>svg]:!min-h-[13px] [&>svg]:!min-w-[13px] [&>svg]:!m-0 [&>svg]:shrink-0'
    : iconSize.value === 16
    ? '[&>svg]:!size-[16px] [&>svg]:!min-h-[16px] [&>svg]:!min-w-[16px] [&>svg]:shrink-0'
    : '[&>svg]:!size-[15px] [&>svg]:!min-h-[15px] [&>svg]:!min-w-[15px] [&>svg]:shrink-0');

const contentClasses = computed(() => [
    'relative z-2 grid min-w-0',
    stateful.value ? 'grid-cols-[minmax(0,1fr)]' : '',
    dropdownDisplay.value || (stateful.value && props.block) ? 'w-full' : '',
    {
        'opacity-0': props.loading
    }
]);

const contentStyle = computed(() =>
{
    if (!stateful.value || props.block || stateContentWidth.value === null)
        return;

    return {
        width: `${stateContentWidth.value}px`
    };
});

const buttonElement = computed(() => element.value?.$el ?? element.value);
const accessibleLabel = computed(() => inlineAsking.value ? confirmationMessage.value : attrs['aria-label'] ?? (iconOnly.value || automaticTrashConfirmation.value || backAction.value ? props.title || buttonText.value || (automaticTrashConfirmation.value ? $t('[[[Usuń]]]') : undefined) : undefined));
const tooltipText = computed(() => inlineAsking.value ? null : (props.title || (backAction.value ? props.text : null)) && (iconOnly.value || backAction.value || toolbarDisplay.value || !buttonText.value) ? props.title || props.text : null);

function resetInlineConfirmation()
{
    inlineAsking.value = false;
}

watch(inlineAsking, (asking, previous, onCleanup) =>
{
    if (!asking)
        return;

    const timeout = window.setTimeout(resetInlineConfirmation, 3000);
    const onPointerDown = (event: PointerEvent) =>
    {
        if (!buttonElement.value?.contains(event.target as Node))
            resetInlineConfirmation();
    };
    const onKeyDown = (event: KeyboardEvent) =>
    {
        if (event.key === 'Escape')
        {
            event.preventDefault();
            event.stopPropagation();
            resetInlineConfirmation();
        }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    buttonElement.value?.addEventListener('keydown', onKeyDown);

    onCleanup(() =>
    {
        window.clearTimeout(timeout);
        document.removeEventListener('pointerdown', onPointerDown, true);
        buttonElement.value?.removeEventListener('keydown', onKeyDown);
    });
});
watch(() => [confirmationMessage.value, resolvedConfirmationMode.value, interactionDisabled.value], resetInlineConfirmation);

function triggerClick(event: MouseEvent)
{
    emit('click', event);
}

function onConfirm(event: MouseEvent)
{
    if (routeTarget.value)
        navigateTo(routeTarget.value);
    else
        triggerClick(event);
}

function onClick(event: MouseEvent)
{
    if (props.stopPropagation)
        event.stopPropagation();

    if (interactionDisabled.value)
    {
        event.preventDefault();

        return;
    }

    if (confirmationMessage.value.length > 0 && resolvedConfirmationMode.value === 'inline')
    {
        event.preventDefault();
        event.stopPropagation();

        if (!inlineAsking.value)
        {
            inlineAsking.value = true;

            return;
        }

        resetInlineConfirmation();
        triggerClick(event);

        return;
    }

    if (confirmationMessage.value.length === 0)
    {
        triggerClick(event);

        if (dropdownDisplay.value && !props.preventClose)
            hideDropdown?.();
    }
}

onMounted(() =>
{
    const root = buttonElement.value;

    dropdown.value = !!root?.closest('.ideo-dropdown-panel');
    floating.value = !!root?.closest('.floating');
    loaded.value = true;
    measureStateContent();
});
onBeforeUnmount(() =>
{
    stateWidthAnimation.value?.cancel();
    cancelStatePositionAnimations();
});
watch(stateful, (value) =>
{
    if (value)
        return;

    stateWidthAnimation.value?.cancel();
    cancelStatePositionAnimations();
    stateContentWidth.value = null;
    stateContentContainer.value?.style.removeProperty('width');
});
useActionButtonAgentTool({
    tool: () => props.tool,
    label: () => props.text || props.title || '',
    disabled: () => interactionDisabled.value,
    confirmation: () => confirmationMessage.value.length > 0,
    click: () => buttonElement.value?.click(),
    warn: (message: string) => useLogging().$log.warn(message)
});
</script>

<template>
    <component
        :is="componentTag"
        ref="element"
        v-bind="rootAttrs"
        :to="routeTarget"
        :type="buttonType"
        :form="attrs.form ?? (buttonType === 'submit' ? actionBarFormId : undefined)"
        :title="inlineAsking ? undefined : tooltipText ? undefined : title"
        :aria-label="accessibleLabel"
        :aria-pressed="resolvedConfirmationMode === 'inline' && confirmationMessage ? inlineAsking : attrs['aria-pressed']"
        :data-armed="inlineAsking || undefined"
        @blur="resetInlineConfirmation"
        :class="[customClasses, attrsClasses]"
        :disabled="interactionDisabled"
        :aria-disabled="ariaDisabled"
        :data-disabled="dataDisabled"
        :aria-busy="loading || undefined"
        :data-loading="loading || undefined"
        :data-action-icon="resolvedIcon || undefined"
        :data-action-variant="props.variant"
        @click="onClick"
    >
        <template v-if="unstyled">
            <IdeoIcon v-if="resolvedIcon && iconPosition === 'left'" :name="resolvedIcon" :class="['shrink-0', iconDimensionClasses, props.iconClass]" :size="iconSize" />
            <span v-if="inlineAsking && inlineTextConfirmation">{{ $t(props.confirmText) }}</span>
            <template v-else>
                <slot v-if="!(inlineAsking && (iconOnly || automaticTrashConfirmation))" name="default" />
                <span v-if="textVisible">{{ buttonText }}</span>
            </template>
            <IdeoIcon v-if="resolvedIcon && iconPosition === 'right'" :name="resolvedIcon" :class="['shrink-0', iconDimensionClasses, props.iconClass]" :size="iconSize" />
        </template>
        <template v-else>
            <span
                v-if="aiVariant && !disabled"
                aria-hidden="true"
                class="pointer-events-none absolute left-1/2 top-1/2 z-0 aspect-square w-[280%] -translate-x-1/2 -translate-y-1/2 animate-[spin_3s_linear_infinite] bg-[conic-gradient(from_0deg,transparent_0_84%,oklch(0.88_0.16_80)_94%,transparent_100%)] dark:bg-[conic-gradient(from_0deg,transparent_0_84%,oklch(0.16_0_0)_94%,transparent_100%)] motion-reduce:animate-none"
            />
            <span
                v-if="aiVariant && !disabled"
                aria-hidden="true"
                class="pointer-events-none absolute inset-[1.5px] z-1 rounded-[7px] bg-[oklch(0.18_0_0)] transition-colors duration-150 group-active:bg-[oklch(0.14_0_0)] dark:bg-[oklch(0.96_0_0)] dark:group-hover:bg-[oklch(0.98_0_0)] dark:group-active:bg-[oklch(0.89_0_0)]"
            />

            <span ref="stateContentContainer" :class="contentClasses" :style="contentStyle" data-action-state-container>
                <Transition
                    :css="false"
                    @enter="enterStateContent"
                    @leave="leaveStateContent"
                >
                    <span
                        :key="contentStateKey"
                        ref="stateContent"
                        :class="['col-start-1 row-start-1 inline-flex min-w-0 items-center', dropdownDisplay ? 'justify-start gap-[10px]' : 'justify-center gap-[6px]', childIconDimensionClasses, stateful ? 'relative h-[30px] shrink-0' : '', stateful && !iconOnly ? 'px-[10px]' : '', stateful && block ? 'w-full' : stateful ? 'w-max' : '']"
                        :aria-live="stateful ? 'polite' : undefined"
                        :aria-atomic="stateful || undefined"
                        data-action-state-content
                    >
                        <IdeoIcon
                            v-if="resolvedIcon && iconPosition === 'left'"
                            :name="resolvedIcon"
                            :class="['shrink-0', iconDimensionClasses, aiVariant && !disabled ? 'text-[oklch(0.82_0.15_80)] dark:!text-[oklch(0.16_0_0)]' : '', props.iconClass]"
                            :size="iconSize"
                        />
                        <span v-if="inlineAsking && inlineTextConfirmation">{{ $t(props.confirmText) }}</span>
                        <template v-else>
                            <slot v-if="!(inlineAsking && (iconOnly || automaticTrashConfirmation))" name="default" />
                            <span v-if="textVisible">{{ buttonText }}</span>
                        </template>
                        <IdeoIcon
                            v-if="resolvedIcon && iconPosition === 'right'"
                            :name="resolvedIcon"
                            :class="['shrink-0', iconDimensionClasses, aiVariant && !disabled ? 'text-[oklch(0.82_0.15_80)] dark:!text-[oklch(0.16_0_0)]' : '', props.iconClass]"
                            :size="iconSize"
                        />
                        <Confirmation
                            v-if="loaded && confirmationMessage.length > 0 && resolvedConfirmationMode === 'popover'"
                            v-bind="confirmAttrs"
                            :message="confirmationMessage"
                            :target="buttonElement"
                            placement="auto"
                            :value="{}"
                            @confirm="onConfirm"
                        />
                    </span>
                </Transition>
            </span>

            <span v-if="loading" class="absolute inset-0 z-3 flex items-center justify-center" aria-hidden="true">
                <Loader class="size-[15px]" :tone="loaderTone" />
            </span>
        </template>
        <IdeoTooltip v-if="tooltipText" external :target="buttonElement" :tooltip="tooltipText" />
    </component>
</template>
